import React, { useState, useEffect } from 'react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { 
  Users, Mail, Download, BarChart3, Shield, CheckCircle, XCircle, 
  RefreshCw, Eye, EyeOff, LogOut, Clock, UserPlus
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminPage() {
  const [apiKey, setApiKey] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [usersData, setUsersData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  
  // Beta Access state
  const [waitlistUsers, setWaitlistUsers] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [betaLoading, setBetaLoading] = useState(false);
  const [approvingUser, setApprovingUser] = useState(null);
  const [manualApproveUsername, setManualApproveUsername] = useState('');
  const [wipeUsername, setWipeUsername] = useState('');
  const [wipingUser, setWipingUser] = useState(false);

  const fetchWithAuth = async (endpoint) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: { 'X-Admin-Key': apiKey }
    });
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Invalid API key');
      }
      throw new Error(`Request failed: ${response.status}`);
    }
    return response.json();
  };

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      // Test the key by fetching users
      const data = await fetchWithAuth('/api/admin/users-emails');
      setUsersData(data);
      setIsAuthenticated(true);
      // Also fetch analytics
      const analytics = await fetchWithAuth('/api/admin/analytics');
      setAnalyticsData(analytics);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setLoading(true);
    try {
      const [users, analytics] = await Promise.all([
        fetchWithAuth('/api/admin/users-emails'),
        fetchWithAuth('/api/admin/analytics')
      ]);
      setUsersData(users);
      setAnalyticsData(analytics);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/export-emails`, {
        headers: { 'X-Admin-Key': apiKey }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tickler_emails.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download CSV');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setApiKey('');
    setUsersData(null);
    setAnalyticsData(null);
  };

  // Beta Access Management Functions
  const fetchBetaData = async () => {
    setBetaLoading(true);
    try {
      const [waitlistRes, approvedRes] = await Promise.all([
        fetchWithAuth('/api/admin/waitlist'),
        fetchWithAuth('/api/admin/approved-users')
      ]);
      setWaitlistUsers(waitlistRes.users || []);
      setApprovedUsers(approvedRes.users || []);
    } catch (err) {
      console.error('Error fetching beta data:', err);
    } finally {
      setBetaLoading(false);
    }
  };

  const approveUser = async (xUsername, userId, xId, email) => {
    setApprovingUser(xUsername || userId || email);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/approve-beta-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': apiKey
        },
        body: JSON.stringify({ 
          x_username: xUsername,
          user_id: userId,
          x_id: xId,
          email: email || null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.welcome_email_sent) {
          alert(`✅ Approved! Welcome email sent to ${data.user?.email}`);
        } else {
          alert(`✅ Approved.\n\nWelcome email: ${data.welcome_email_status || 'not sent (no real email on file)'}`);
        }
        fetchBetaData();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to approve user'}`);
      }
    } catch (err) {
      console.error('Error approving user:', err);
      alert('Failed to approve user');
    } finally {
      setApprovingUser(null);
    }
  };

  const revokeUser = async (identifier) => {
    if (!window.confirm(`Revoke beta access for ${identifier}?`)) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/revoke-beta-user/${encodeURIComponent(identifier)}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': apiKey }
      });
      
      if (response.ok) {
        fetchBetaData();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to revoke access'}`);
      }
    } catch (err) {
      console.error('Error revoking user:', err);
      alert('Failed to revoke access');
    }
  };

  const approveAllExisting = async () => {
    if (!window.confirm('This will approve ALL existing users for beta access. Continue?')) return;
    
    setBetaLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/approve-all-existing-users`, {
        method: 'POST',
        headers: { 'X-Admin-Key': apiKey }
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`Approved ${data.newly_approved} existing users for beta!`);
        fetchBetaData();
      }
    } catch (err) {
      console.error('Error approving all users:', err);
      alert('Failed to approve existing users');
    } finally {
      setBetaLoading(false);
    }
  };

  const handleManualApprove = async (e) => {
    e.preventDefault();
    const value = manualApproveUsername.trim();
    if (!value) return;
    
    const stripped = value.replace(/^@/, '');
    if (stripped.includes('@') && stripped.split('@').pop().includes('.')) {
      // Looks like an email address — approve by email (triggers welcome email)
      await approveUser(null, null, null, stripped.toLowerCase());
    } else if (/\.(com|net|org|io|co|edu|gov)$/i.test(stripped)) {
      alert(`⚠️ "${stripped}" looks like an email address MISSING its @ sign.\n\nNothing was approved. Please re-type the full email (e.g. name@gmail.com) so the welcome email can be sent.`);
      return;
    } else {
      await approveUser(stripped);
    }
    setManualApproveUsername('');
  };

  const resetBetaData = async (wipeUsers = false) => {
    const confirmMsg = wipeUsers 
      ? 'This will DELETE ALL USERS, waitlist, and approved users. This CANNOT be undone. Are you absolutely sure?'
      : 'This will WIPE all waitlist and approved users data. Are you sure?';
    if (!window.confirm(confirmMsg)) return;
    if (wipeUsers && !window.confirm('FINAL WARNING: ALL USER ACCOUNTS WILL BE DELETED. Click OK to proceed.')) return;
    
    setBetaLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/reset-beta-data?wipe_users=${wipeUsers}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': apiKey }
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`Data reset! Deleted: ${data.deleted.waitlist_entries} waitlist, ${data.deleted.approved_users} approved, ${data.deleted.users_deleted || 0} users`);
        fetchBetaData();
        if (wipeUsers) {
          refreshData(); // Refresh users list too
        }
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to reset'}`);
      }
    } catch (err) {
      console.error('Error resetting beta data:', err);
      alert('Failed to reset beta data');
    } finally {
      setBetaLoading(false);
    }
  };

  // Wipe single user account completely
  const wipeUserAccount = async (e) => {
    e.preventDefault();
    const username = wipeUsername.trim().replace('@', '');
    if (!username) return;
    
    if (!window.confirm(`This will COMPLETELY DELETE all data for "${username}" including:\n\n• User account\n• All ideas\n• Sessions\n• Email preferences\n• Watchlists\n• Research schedule\n• Files\n• Import history\n• Waitlist/beta status\n• Groups\n\nThey will need to log in fresh. Continue?`)) return;
    
    setWipingUser(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/wipe-user/${encodeURIComponent(username)}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': apiKey }
      });
      
      if (response.ok) {
        const data = await response.json();
        const deletedSummary = Object.entries(data.deleted)
          .filter(([_, count]) => count > 0)
          .map(([key, count]) => `${key}: ${count}`)
          .join(', ');
        alert(`✅ User "${username}" wiped!\n\nDeleted: ${deletedSummary || 'User record'}\n\nThey can now log in fresh to test the free import flow.`);
        setWipeUsername('');
        fetchBetaData();
        refreshData();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to wipe user'}`);
      }
    } catch (err) {
      console.error('Error wiping user:', err);
      alert('Failed to wipe user');
    } finally {
      setWipingUser(false);
    }
  };

  // Load beta data when switching to beta tab
  useEffect(() => {
    if (activeTab === 'beta' && isAuthenticated && apiKey) {
      fetchBetaData();
    }
  }, [activeTab, isAuthenticated, apiKey]);

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800 border-slate-700">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-orange-500" />
            </div>
            <CardTitle className="text-2xl text-white">Admin Access</CardTitle>
            <p className="text-slate-400 text-sm mt-2">Enter your admin API key to continue</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Input
                type={showKey ? 'text' : 'password'}
                placeholder="Enter Admin API Key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white pr-10"
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              />
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            
            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm">
                {error}
              </div>
            )}
            
            <Button 
              onClick={handleLogin} 
              disabled={!apiKey || loading}
              className="w-full bg-orange-500 hover:bg-orange-600"
            >
              {loading ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Verifying...</>
              ) : (
                'Access Admin Panel'
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Tickler Admin</h1>
              <p className="text-slate-400 text-sm">Management Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={refreshData}
              disabled={loading}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="text-slate-400 hover:text-white hover:bg-slate-700"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        {usersData && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{usersData.summary.total_users}</p>
                    <p className="text-slate-400 text-sm">Total Users</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{usersData.summary.real_emails}</p>
                    <p className="text-slate-400 text-sm">Real Emails</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{usersData.summary.oauth_emails}</p>
                    <p className="text-slate-400 text-sm">OAuth Emails</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{usersData.summary.weekly_digest_enabled}</p>
                    <p className="text-slate-400 text-sm">Weekly Digest</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={activeTab === 'users' ? 'default' : 'outline'}
            onClick={() => setActiveTab('users')}
            className={activeTab === 'users' ? 'bg-orange-500' : 'border-slate-600 text-slate-300'}
          >
            <Users className="w-4 h-4 mr-2" />
            Users & Emails
          </Button>
          <Button
            variant={activeTab === 'beta' ? 'default' : 'outline'}
            onClick={() => setActiveTab('beta')}
            className={activeTab === 'beta' ? 'bg-orange-500' : 'border-slate-600 text-slate-300'}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Beta Access
          </Button>
        </div>
        
        {/* Link to Full Dashboard */}
        <div className="mb-6 p-4 bg-slate-700/50 rounded-lg border border-slate-600">
          <p className="text-slate-300 text-sm mb-2">
            For full analytics (Growth, Retention, Revenue, Engagement, etc.), open the main app and click the Dashboard button in your profile menu.
          </p>
          <a 
            href="/" 
            className="text-orange-400 hover:text-orange-300 text-sm font-medium"
          >
            Go to Main App →
          </a>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && usersData && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">User Emails</CardTitle>
              <Button 
                onClick={downloadCSV}
                size="sm"
                className="bg-green-600 hover:bg-green-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">User</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Email</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Source</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Digest</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersData.users.map((user, idx) => (
                      <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium">{user.name || 'N/A'}</p>
                            <p className="text-slate-400 text-sm">@{user.x_username || 'N/A'}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className={user.is_real_email ? 'text-green-400' : 'text-slate-500'}>
                              {user.email || user.digest_email || 'Not set'}
                            </span>
                            {user.is_real_email ? (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : (
                              <XCircle className="w-4 h-4 text-slate-500" />
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {user.email_from_x_oauth ? (
                            <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs">
                              X OAuth
                            </span>
                          ) : user.is_real_email ? (
                            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
                              Manual
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-slate-500/20 text-slate-400 rounded text-xs">
                              Generated
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2">
                            {user.daily_digest_enabled && (
                              <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
                                Daily
                              </span>
                            )}
                            {user.weekly_digest_enabled && (
                              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
                                Weekly
                              </span>
                            )}
                            {!user.daily_digest_enabled && !user.weekly_digest_enabled && (
                              <span className="text-slate-500 text-sm">None</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-400 text-sm">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Beta Access Tab */}
        {activeTab === 'beta' && (
          <div className="space-y-6">
            {/* Header with actions */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Beta Access Management</h3>
                <p className="text-sm text-slate-400">Manage who can access the platform during beta</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={fetchBetaData}
                  disabled={betaLoading}
                  variant="outline"
                  size="sm"
                  className="border-slate-600 text-slate-300"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${betaLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button
                  onClick={approveAllExisting}
                  disabled={betaLoading}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve All Existing
                </Button>
                <Button
                  onClick={() => resetBetaData(false)}
                  disabled={betaLoading}
                  size="sm"
                  variant="outline"
                  className="border-red-900/50 text-red-400 hover:bg-red-900/30"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reset Beta Data
                </Button>
                <Button
                  onClick={() => resetBetaData(true)}
                  disabled={betaLoading}
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Wipe ALL Users
                </Button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-400">{waitlistUsers.length}</p>
                      <p className="text-slate-400 text-sm">On Waitlist</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-400">{approvedUsers.length}</p>
                      <p className="text-slate-400 text-sm">Approved</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Approve */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg">Quick Approve by X Username or Email</CardTitle>
                <p className="text-slate-400 text-sm">Approving by email sends the welcome email automatically</p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleManualApprove} className="flex gap-2">
                  <Input
                    type="text"
                    value={manualApproveUsername}
                    onChange={(e) => setManualApproveUsername(e.target.value)}
                    placeholder="@username or email@example.com"
                    data-testid="quick-approve-input"
                    className="flex-1 bg-slate-700 border-slate-600 text-white"
                  />
                  <Button
                    type="submit"
                    disabled={!manualApproveUsername.trim() || approvingUser}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Approve
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Wipe User Account - For Testing */}
            <Card className="bg-slate-800 border-red-900/50">
              <CardHeader>
                <CardTitle className="text-lg text-red-400">🧹 Wipe User Account (Testing)</CardTitle>
                <p className="text-slate-400 text-sm">Completely delete a user&apos;s data so they can test the free import flow from scratch</p>
              </CardHeader>
              <CardContent>
                <form onSubmit={wipeUserAccount} className="flex gap-2">
                  <Input
                    type="text"
                    value={wipeUsername}
                    onChange={(e) => setWipeUsername(e.target.value)}
                    placeholder="Enter email or @username"
                    className="flex-1 bg-slate-700 border-slate-600 text-white"
                  />
                  <Button
                    type="submit"
                    disabled={!wipeUsername.trim() || wipingUser}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {wipingUser ? 'Wiping...' : 'Wipe Account'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Waitlist */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  Waitlist ({waitlistUsers.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {waitlistUsers.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No users on waitlist</p>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {waitlistUsers.map((u, idx) => (
                      <div 
                        key={u.user_id || u.x_id || idx}
                        className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-slate-600">
                            <span className="text-sm font-medium text-slate-300">
                              {(u.x_username || u.x_display_name || '?')[0].toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-white">
                              {u.x_display_name || u.x_username || 'Unknown'}
                            </p>
                            <p className="text-xs text-slate-400">
                              @{u.x_username || 'no-username'} • Joined {new Date(u.joined_at).toLocaleDateString()}
                            </p>
                            {u.email && !u.email.endsWith('@x.com') ? (
                              <p className="text-xs text-blue-400">{u.email}</p>
                            ) : (
                              <p className="text-xs text-amber-500">no email on file — no welcome email will be sent</p>
                            )}
                          </div>
                        </div>
                        <Button
                          onClick={() => approveUser(u.x_username, u.user_id, u.x_id, u.email && !u.email.endsWith('@x.com') ? u.email : null)}
                          disabled={approvingUser === (u.x_username || u.user_id)}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {approvingUser === (u.x_username || u.user_id) ? 'Approving...' : 'Approve'}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Approved Users */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Approved Users ({approvedUsers.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {approvedUsers.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No approved users yet</p>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {approvedUsers.map((u, idx) => (
                      <div 
                        key={u.user_id || u.x_id || idx}
                        className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-green-500/20">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{u.x_username ? `@${u.x_username}` : (u.email || 'unknown')}</p>
                            <p className="text-xs text-slate-400">
                              Approved {new Date(u.approved_at).toLocaleDateString()} • {u.approved_by || 'admin'}
                              {u.welcome_email_sent_at && <span className="text-green-400"> • ✉ Welcomed</span>}
                            </p>
                            {u.email && u.x_username && (
                              <p className="text-xs text-blue-400">{u.email}</p>
                            )}
                          </div>
                        </div>
                        <Button
                          onClick={() => revokeUser(u.x_username || u.user_id || u.email)}
                          variant="outline"
                          size="sm"
                          className="border-red-900/50 text-red-400 hover:bg-red-900/30"
                        >
                          Revoke
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
